import React from "react";
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  spring,
  interpolate,
  Sequence,
  Audio,
  Img,
  staticFile,
  random,
} from "remotion";
import {SPRINGS, FONT_SCALE, SAFE_ZONES} from "../presets/editing-styles";
import {CLAMP, FONT_STACK} from "../presets/idv";
import {Noise} from "../components/dimus/Noise";
import {Vignette} from "../components/dimus/Vignette";
import {FilmGrain} from "../components/dimus/FilmGrain";
import {GradientText} from "../components/dimus/GradientText";
import {SceneTransition} from "../components/dimus/SceneTransition";
import {OrganicLayer} from "../components/overlays/OrganicLayer";

/**
 * O QUE EU APRENDI ERRANDO #03 — "Perdi Cliente por Over-Delivery"
 * Editing Style: CINEMATIC
 * Profile: @guilhermeribeiro.ai
 * Audio: ElevenLabs TTS per-scene (voz Guilherme)
 */

const TEAL = "#2dd4bf";
const TEAL_DARK = "#052020";
const WHITE = "#f5f5f7";
const MUTED = "#a3a3a3";
const RED = "#F87171";
const AMBER = "#f5ad43";
const GREEN = "#00DF82";

const SZ = SAFE_ZONES.universal;

const SCENES = [
  {dur: 78, file: "audio/aprendi03/aprendi03_c01.mp3", dir: "right" as const},
  {dur: 454, file: "audio/aprendi03/aprendi03_c02.mp3", dir: "left" as const},
  {dur: 302, file: "audio/aprendi03/aprendi03_c03.mp3", dir: "up" as const},
  {dur: 369, file: "audio/aprendi03/aprendi03_c04.mp3", dir: "right" as const},
  {dur: 141, file: "audio/aprendi03/aprendi03_c05.mp3", dir: "up" as const},
];

export const APRENDI03_TOTAL = SCENES.reduce((s, sc) => s + sc.dur, 0);

// ─── Particles ────────────────────────────────────────────────────────
const Particles: React.FC<{count: number; color: string}> = ({count, color}) => {
  const frame = useCurrentFrame();
  return (
    <>
      {Array.from({length: count}, (_, i) => {
        const x = random(`px-${i}`) * 1080;
        const baseY = random(`py-${i}`) * 1920;
        const size = 2 + random(`ps-${i}`) * 3;
        const speed = 0.2 + random(`psp-${i}`) * 0.4;
        const wrapped = ((baseY - frame * speed) % 2100 + 2100) % 2100 - 100;
        return (
          <div key={i} style={{
            position: "absolute", left: x, top: wrapped,
            width: size, height: size, borderRadius: "50%",
            background: color, opacity: 0.08 + random(`po-${i}`) * 0.12,
          }} />
        );
      })}
    </>
  );
};

// ─── Delivery stack item ──────────────────────────────────────────────
const DeliveryItem: React.FC<{text: string; delay: number; overflow?: boolean}> = ({text, delay, overflow}) => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const p = spring({frame: frame - delay, fps, config: SPRINGS.snappy});

  if (frame < delay) return null;
  return (
    <div style={{
      opacity: p,
      transform: `translateY(${interpolate(p, [0, 1], [20, 0], CLAMP)}px)`,
      padding: "14px 28px", borderRadius: 12,
      background: overflow ? "rgba(248,113,113,0.12)" : "rgba(45,212,191,0.08)",
      border: `1px solid ${overflow ? "rgba(248,113,113,0.3)" : "rgba(45,212,191,0.15)"}`,
      fontSize: FONT_SCALE.body.min, fontWeight: 600,
      color: overflow ? RED : TEAL,
      fontFamily: FONT_STACK,
    }}>
      {overflow && <span style={{marginRight: 8}}>+</span>}
      {text}
    </div>
  );
};

// ─── SCENE 1: HOOK ────────────────────────────────────────────────────
const S1: React.FC = () => (
  <AbsoluteFill style={{justifyContent: "center", alignItems: "center", padding: `${SZ.top}px ${SZ.left}px ${SZ.bottom}px`}}>
    <Audio src={staticFile(SCENES[0].file)} volume={0.9} />
    <GradientText text="APRENDI #03" colors={[TEAL, AMBER]} fontSize={FONT_SCALE.h1.max} springProfile="bouncy" />
  </AbsoluteFill>
);

// ─── SCENE 2: CONTEXTO — over-delivery ───────────────────────────────
const S2: React.FC = () => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const titleP = spring({frame: frame - 10, fps, config: SPRINGS.heavy});

  return (
    <OrganicLayer intensity="subtle" seed="aprendi-03">
    <AbsoluteFill style={{justifyContent: "center", alignItems: "center", padding: `${SZ.top}px ${SZ.left}px ${SZ.bottom}px`, flexDirection: "column", gap: 20}}>
      <Audio src={staticFile(SCENES[1].file)} volume={0.9} />
      <div style={{
        opacity: titleP, fontSize: FONT_SCALE.h2.min, fontWeight: 700,
        color: WHITE, fontFamily: FONT_STACK, textAlign: "center", lineHeight: 1.2,
      }}>
        Projeto de 3 meses.{"\n"}Escopo fechado.
      </div>
      <div style={{display: "flex", flexDirection: "column", gap: 12, alignItems: "center"}}>
        <DeliveryItem text="Escopo original" delay={60} />
        <DeliveryItem text="+ automação extra" delay={120} overflow />
        <DeliveryItem text="+ relatório extra" delay={180} overflow />
        <DeliveryItem text="+ reunião extra" delay={240} overflow />
      </div>
      <div style={{
        opacity: spring({frame: frame - 340, fps, config: SPRINGS.smooth}),
        fontSize: FONT_SCALE.body.min, fontWeight: 500, color: MUTED,
        fontFamily: FONT_STACK, textAlign: "center",
      }}>
        Achei que ia renovar fácil.
      </div>
    </AbsoluteFill>
    </OrganicLayer>
  );
};

// ─── SCENE 3: CONSEQUÊNCIA ───────────────────────────────────────────
const S3: React.FC = () => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const p1 = spring({frame: frame - 5, fps, config: SPRINGS.heavy});
  const p2 = spring({frame: frame - 100, fps, config: SPRINGS.smooth});

  return (
    <AbsoluteFill style={{justifyContent: "center", alignItems: "center", padding: `${SZ.top}px ${SZ.left}px ${SZ.bottom}px`, flexDirection: "column", gap: 28}}>
      <Audio src={staticFile(SCENES[2].file)} volume={0.9} />
      <div style={{
        opacity: p1,
        transform: `scale(${interpolate(p1, [0, 1], [0.9, 1], CLAMP)})`,
        fontSize: FONT_SCALE.h1.min, fontWeight: 900, color: RED,
        fontFamily: FONT_STACK, textAlign: "center", lineHeight: 1.1,
      }}>
        Não renovou.
      </div>
      <div style={{
        opacity: p2, fontSize: FONT_SCALE.body.min, fontWeight: 500,
        color: MUTED, fontFamily: FONT_STACK, textAlign: "center",
        lineHeight: 1.4, maxWidth: "88%",
      }}>
        Quando você entrega tudo de graça,{"\n"}o cliente não sabe mais o que é{"\n"}serviço e o que é favor.
      </div>
    </AbsoluteFill>
  );
};

// ─── SCENE 4: APRENDIZADO ────────────────────────────────────────────
const S4: React.FC = () => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const p1 = spring({frame: frame - 5, fps, config: SPRINGS.heavy});
  const p2 = spring({frame: frame - 60, fps, config: SPRINGS.smooth});
  const p3 = spring({frame: frame - 180, fps, config: SPRINGS.bouncy});

  return (
    <AbsoluteFill style={{justifyContent: "center", alignItems: "flex-start", padding: `${SZ.top}px ${SZ.left}px ${SZ.bottom}px`, flexDirection: "column", gap: 20}}>
      <Audio src={staticFile(SCENES[3].file)} volume={0.9} />
      <div style={{
        opacity: p1, transform: `translateX(${interpolate(p1, [0, 1], [-40, 0], CLAMP)}px)`,
        fontSize: FONT_SCALE.h2.min, fontWeight: 700, color: WHITE, fontFamily: FONT_STACK,
      }}>
        Over-delivery sem controle{"\n"}= desvalorização.
      </div>
      <div style={{
        opacity: p2, fontSize: FONT_SCALE.body.min, fontWeight: 500,
        color: MUTED, fontFamily: FONT_STACK, lineHeight: 1.4, maxWidth: "92%",
      }}>
        O cliente não percebe que tá recebendo mais.{"\n"}Só percebe quando para de receber.
      </div>
      <div style={{display: "flex", gap: 12, alignItems: "baseline", flexWrap: "wrap", marginTop: 8}}>
        <GradientText text="Combinado" colors={[GREEN, TEAL]} fontSize={FONT_SCALE.h1.min} delay={160} springProfile="bouncy" />
        <div style={{opacity: p3, fontSize: FONT_SCALE.h2.min, fontWeight: 700, color: WHITE, fontFamily: FONT_STACK}}>bem feito.</div>
      </div>
      <div style={{
        opacity: p3, fontSize: FONT_SCALE.body.min, fontWeight: 800,
        color: AMBER, fontFamily: FONT_STACK, marginTop: 4,
      }}>
        O extra, cobra separado.
      </div>
    </AbsoluteFill>
  );
};

// ─── SCENE 5: CTA ────────────────────────────────────────────────────
const S5: React.FC = () => {
  const frame = useCurrentFrame();
  const {fps} = useVideoConfig();
  const profileP = spring({frame: frame - 8, fps, config: SPRINGS.snappy});
  const btnP = spring({frame: frame - 50, fps, config: SPRINGS.bouncy});
  const pulse = frame > 80 ? 1 + Math.sin((frame - 80) * 0.08) * 0.02 : 1;

  return (
    <AbsoluteFill style={{justifyContent: "center", alignItems: "center", gap: 20, flexDirection: "column", padding: `${SZ.top}px ${SZ.left}px ${SZ.bottom}px`}}>
      <Audio src={staticFile(SCENES[4].file)} volume={0.9} />
      <div style={{
        opacity: profileP, fontSize: FONT_SCALE.body.min, fontWeight: 500,
        color: MUTED, fontFamily: FONT_STACK, textAlign: "center", lineHeight: 1.3,
        transform: `translateY(${interpolate(profileP, [0, 1], [15, 0], CLAMP)}px)`,
      }}>
        Já perdeu cliente tentando agradar demais?
      </div>
      <div style={{
        opacity: profileP, display: "flex", alignItems: "center", gap: 16,
        transform: `translateY(${interpolate(profileP, [0, 1], [20, 0], CLAMP)}px)`,
      }}>
        <div style={{
          width: 64, height: 64, borderRadius: "50%", overflow: "hidden",
          border: `3px solid ${TEAL}`, boxShadow: `0 0 20px rgba(45,212,191,0.3)`,
        }}>
          <Img src={staticFile("assets/profiles/guilhermeribeiro-ai.jpg")}
            style={{width: "100%", height: "100%", objectFit: "cover"}} />
        </div>
        <div>
          <div style={{fontSize: FONT_SCALE.body.min, fontWeight: 700, color: WHITE, fontFamily: FONT_STACK}}>@guilhermeribeiro.ai</div>
          <div style={{fontSize: FONT_SCALE.label.min, fontWeight: 400, color: MUTED, fontFamily: FONT_STACK}}>Bastidores reais</div>
        </div>
      </div>
      <div style={{
        opacity: btnP,
        transform: `translateY(${interpolate(btnP, [0, 1], [30, 0], CLAMP)}px) scale(${interpolate(btnP, [0, 1], [0.5, 1], CLAMP) * pulse})`,
        padding: "18px 52px", borderRadius: 999, background: TEAL,
        fontSize: FONT_SCALE.cta.max, fontWeight: 700, color: "#000", fontFamily: FONT_STACK,
        boxShadow: `0 0 40px rgba(45,212,191,0.4)`,
      }}>
        COMENTA E SEGUE
      </div>
    </AbsoluteFill>
  );
};

// ─── MAIN ─────────────────────────────────────────────────────────────
export const Aprendi03: React.FC = () => {
  let offset = 0;
  return (
    <AbsoluteFill style={{backgroundColor: "#000"}}>
      <div style={{position: "absolute", inset: 0, background: `radial-gradient(ellipse at 50% 35%, ${TEAL_DARK}, #000 70%)`}} />
      <Noise opacity={0.02} />
      <Particles count={25} color={TEAL} />
      <FilmGrain opacity={0.04} />
      <Vignette intensity={0.7} />

      {SCENES.map(({dur, dir}, i) => {
        const from = offset;
        offset += dur;
        const Comp = [S1, S2, S3, S4, S5][i];
        return (
          <Sequence key={i} from={from} durationInFrames={dur}>
            <SceneFade duration={dur}>
              <SceneTransition type="in" direction={dir} springProfile="smooth">
                <Comp />
              </SceneTransition>
            </SceneFade>
          </Sequence>
        );
      })}
    </AbsoluteFill>
  );
};

const SceneFade: React.FC<{duration: number; children: React.ReactNode}> = ({duration, children}) => {
  const frame = useCurrentFrame();
  const fadeIn = interpolate(frame, [0, 8], [0, 1], CLAMP);
  const fadeOut = interpolate(frame, [duration - 10, duration], [1, 0], CLAMP);
  return <AbsoluteFill style={{opacity: Math.min(fadeIn, fadeOut)}}>{children}</AbsoluteFill>;
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
